import React from 'react'
import { ThreePanelLayout } from '../layouts/threePanel'
import { Teacher } from '../providers/context'
import { Loader } from '../components/simple/loader'
import { TextInput } from '../components/form/textInput'
import { LocalTable } from '../components/table'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../request'
import { ColumnDef } from '@tanstack/react-table'
import { DateWrapper } from '../components/simple/date'
import { Button, ButtonShape } from '../components/simple/button'
import { IconTimes } from '../icons/library'

type StudentWithoutReachableGuardian = {
  name: string
  school: string
  importedID?: string
}

type UnreachablePerson = {
  name: string
  type: 'student' | 'guardian'
  school: string
  importedID?: string
}

type FailedContact = {
  name: string
  type: 'student' | 'guardian'
  school: string
  failedContact: string
  info: string
  importedID?: string
}

type EditedContact = {
  id: number
  name: string
  type: 'student' | 'guardian'
  school: string
  contactType: string
  newValue: string
  editedBy: string
  importedID?: string
  date?: string
}

type NoSchoolContact = {
  name: string
  type: string
  importedID: string
}

export const ContactsReportPage: React.FC<{ user: Teacher }> = ({ user }) => {
  const { data, reload } = useRequest<{
    studentsWithoutReachableGuardians: StudentWithoutReachableGuardian[]
    unreachablePeople: UnreachablePerson[]
    failedContacts: FailedContact[]
    editedContacts: EditedContact[]
    noSchoolContacts?: NoSchoolContact[]
  }>('/frontend_api/getContactsReport')
  return (
    <ThreePanelLayout noMobile user={user} left={<></>}>
      {data === undefined ? (
        <Loader />
      ) : (
        <div className="flex flex-col items-start gap-4 overflow-auto bg-lightBlue p-4">
          <StudentsWithoutReachableGuardiansTable data={data.studentsWithoutReachableGuardians} />
          <UnreachablePeopleTable data={data.unreachablePeople} />
          <FailedContactsTable data={data.failedContacts} />
          <EditedContactsTable data={data.editedContacts} reload={reload} />
          {data.noSchoolContacts && <NoSchoolContactsTable data={data.noSchoolContacts} />}
        </div>
      )}
    </ThreePanelLayout>
  )
}

const studentWithoutReachableGuardiansColumns: ColumnDef<StudentWithoutReachableGuardian>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
  },
  {
    accessorKey: 'school',
    header: 'School',
  },
  {
    accessorKey: 'importedID',
    header: 'Imported ID',
  },
]

const StudentsWithoutReachableGuardiansTable: React.FC<{ data: StudentWithoutReachableGuardian[] }> = ({ data }) => {
  const [search, setSearch] = React.useState<string>('')
  return (
    <div className="flex w-full flex-col gap-2 rounded-lg bg-white p-4 shadow-lg">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="ml-1 text-xl font-bold">Students without any reachable guardians</div>
        <TextInput placeholder="Search" value={search} onChange={setSearch} />
      </div>
      <LocalTable
        pagination={10}
        search={search}
        columns={studentWithoutReachableGuardiansColumns}
        data={data}
        exportName="studentsWithoutReachableGuardians"
      />
    </div>
  )
}

const unreachablePeopleColumns: ColumnDef<UnreachablePerson>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
  },
  {
    accessorKey: 'type',
    header: 'Type',
  },
  {
    accessorKey: 'school',
    header: 'School',
    cell: ({ cell }) => <div className="whitespace-pre-wrap">{cell.getValue<string>()}</div>,
  },
  {
    accessorKey: 'importedID',
    header: 'Imported ID',
  },
]

const UnreachablePeopleTable: React.FC<{ data: UnreachablePerson[] }> = ({ data }) => {
  const [search, setSearch] = React.useState<string>('')
  return (
    <div className="flex w-full flex-col gap-2 rounded-lg bg-white p-4 shadow-lg">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="ml-1 text-xl font-bold">Unreachable People</div>
        <TextInput placeholder="Search" value={search} onChange={setSearch} />
      </div>
      <LocalTable
        pagination={10}
        search={search}
        columns={unreachablePeopleColumns}
        data={data}
        exportName="unreachablePeople"
      />
    </div>
  )
}

const failedContactsColumns: ColumnDef<FailedContact>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
  },
  {
    accessorKey: 'failedContact',
    header: 'Failed Contact',
  },
  {
    accessorKey: 'info',
    header: 'Additional Info',
  },
  {
    accessorKey: 'type',
    header: 'Type',
  },
  {
    accessorKey: 'school',
    header: 'School',
    cell: ({ cell }) => <div className="whitespace-pre-wrap">{cell.getValue<string>()}</div>,
  },
  {
    accessorKey: 'importedID',
    header: 'Imported ID',
  },
]

const FailedContactsTable: React.FC<{ data: FailedContact[] }> = ({ data }) => {
  const [search, setSearch] = React.useState<string>('')
  return (
    <div className="flex w-full flex-col gap-2 rounded-lg bg-white p-4 shadow-lg">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="ml-1 text-xl font-bold">Failed Contact Info</div>
        <TextInput placeholder="Search" value={search} onChange={setSearch} />
      </div>
      <LocalTable
        pagination={10}
        search={search}
        columns={failedContactsColumns}
        data={data}
        exportName="failedContacts"
      />
    </div>
  )
}

const EditedContactsTable: React.FC<{ data: EditedContact[]; reload: () => void }> = ({ data, reload }) => {
  const [search, setSearch] = React.useState<string>('')
  return (
    <div className="flex w-full flex-col gap-2 rounded-lg bg-white p-4 shadow-lg">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="ml-1 text-xl font-bold">Edited Contact Info</div>
        <TextInput placeholder="Search" value={search} onChange={setSearch} />
      </div>
      <LocalTable<EditedContact>
        pagination={10}
        search={search}
        columns={React.useMemo(
          () => [
            {
              id: 'name',
              accessorFn: (row) => `${row.name} (${row.type})`,
              header: 'Name',
            },
            {
              accessorKey: 'school',
              header: 'School',
              cell: ({ cell }) => <div className="whitespace-pre-wrap">{cell.getValue<string>()}</div>,
            },
            {
              accessorKey: 'contactType',
              header: 'Type',
            },
            {
              accessorKey: 'newValue',
              header: 'New Value',
            },
            {
              accessorKey: 'editedBy',
              header: 'Edited By',
            },
            {
              accessorKey: 'importedID',
              header: 'Imported ID',
            },
            {
              accessorKey: 'date',
              cell: ({ row }) => <>{row.original.date ? <DateWrapper date={row.original.date} /> : 'unknown'}</>,
              header: 'Date',
            },
            {
              header: 'Delete',
              cell: ({ row }) => (
                <Button
                  logo
                  shape={ButtonShape.iconSmall}
                  onClick={() => {
                    makeRequest('/frontend_api/deleteEditedContact', { id: row.original.id })
                      .then(reload)
                      .catch(showAxiosErrorAsToast)
                  }}
                >
                  <IconTimes />
                </Button>
              ),
            },
          ],
          [reload]
        )}
        data={data}
        exportName="editedContacts"
      />
    </div>
  )
}

const noSchoolContactsColumns: ColumnDef<NoSchoolContact>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
  },
  {
    accessorKey: 'type',
    header: 'Type',
  },
  {
    accessorKey: 'importedID',
    header: 'Imported ID',
  },
]

const NoSchoolContactsTable: React.FC<{ data: NoSchoolContact[] }> = ({ data }) => {
  const [search, setSearch] = React.useState<string>('')
  return (
    <div className="flex w-full flex-col gap-2 rounded-lg bg-white p-4 shadow-lg">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="ml-1 text-xl font-bold">Contacts at no schools</div>
        <TextInput placeholder="Search" value={search} onChange={setSearch} />
      </div>
      <LocalTable
        pagination={10}
        search={search}
        columns={noSchoolContactsColumns}
        data={data}
        exportName="noSchoolContacts"
      />
    </div>
  )
}
